import {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  useImperativeHandle,
  forwardRef,
  MouseEvent,
  useCallback,
} from "react";
import { TvVirtualKeyboard } from "../TVKeyboard";
import { isTvEnv } from "@/utils/common.helpers";
import { useTvNumericInput } from "@/hooks/useTvNumericInput";

/**
 * OTP inputs often feel clunky with focus jumps, bad autofill, and weird mobile keyboards.
 * This component uses one hidden input to capture everything and shows mirrored read-only boxes,
 * making typing and autofill smooth and consistent.
 */

/**
 * Focus an element without making the page scroll.
 */
function focusWithoutScroll(element: HTMLElement | null | undefined) {
  if (!element) return;
  try {
    element.focus({ preventScroll: true } as any);
  } catch (_err) {
    const x = window.scrollX;
    const y = window.scrollY;
    element.focus();
    window.scrollTo(x, y);
  }
}

/**
 * Delay focusing to help virtual keyboards show reliably on mobile.
 */
function deferFocus(element: HTMLElement | null | undefined) {
  if (!element) return;
  requestAnimationFrame(() => {
    focusWithoutScroll(element);
  });
}

export interface OTPInputRef {
  clearInputs: () => void;
}

export const OTPInput = forwardRef<
  OTPInputRef,
  {
    onInputChange: () => void;
    onInputFocus: () => void;
    onInputBlur: () => void;
    shouldShowKeyboard?: boolean;
    onDownArrowFromLastRow?: () => void;
    errorMessage?: string;
  }
>(
  (
    {
      onInputChange,
      onInputFocus,
      onInputBlur,
      shouldShowKeyboard,
      onDownArrowFromLastRow,
      errorMessage,
    },
    ref,
  ) => {
    const [otpRaw, setOtpRaw] = useState<string>("");
    const [input1, setInput1] = useState<string>("");
    const [input2, setInput2] = useState<string>("");
    const [input3, setInput3] = useState<string>("");
    const [input4, setInput4] = useState<string>("");
    const [shouldShowOnScreenKeyboard, setShouldShowOnScreenKeyboard] =
      useState(shouldShowKeyboard);
    const [tempValue, setTempValue] = useState<string>("");

    /** Helper to update all 4 input digits at once */
    const setInputDigits = (digits: string) => {
      setInput1(digits[0] ?? "");
      setInput2(digits[1] ?? "");
      setInput3(digits[2] ?? "");
      setInput4(digits[3] ?? "");
    };

    const collectorRef = useRef<HTMLInputElement>(null);
    const input1Ref = useRef<HTMLInputElement>(null);
    const input2Ref = useRef<HTMLInputElement>(null);
    const input3Ref = useRef<HTMLInputElement>(null);
    const input4Ref = useRef<HTMLInputElement>(null);

    const baseInputClasses =
      "flex md:w-[4rem] md:h-[4rem]  h-[3.25rem] w-[3.25rem] items-center justify-center rounded-lg border-none bg-transparent text-center outline outline-1 focus:outline-white caret-white font-semibold text-xl";

    /**
     * Bring up the keyboard on the hidden input using various tricks.
     * Covers weird mobile quirks by clicking and setting caret.
     */
    const activateCollector = () => {
      const el = collectorRef.current;
      if (!el) return;

      // On TV, don't focus the input to prevent native keyboard from opening
      if (isTvEnv()) {
        return;
      }

      // Focus the hidden input without scrolling the page (helps iOS Safari)
      focusWithoutScroll(el);
      try {
        // Place the caret at the end so new digits append correctly
        const len = el.value ? el.value.length : 0;
        el.setSelectionRange(len, len);
      } catch (_e) {
        // Some input types/browsers may throw; safe to ignore
      }
      try {
        // Programmatically "tap" the input so some devices bring up the keyboard
        el.click();
      } catch (_e) {}
      try {
        // Nudge Android Chrome's VirtualKeyboard API to show the keyboard (best-effort)
        (navigator as any).virtualKeyboard?.show?.();
      } catch (_e) {}

      // Some devices need a quick refocus after a tick to reliably show the keyboard
      setTimeout(() => {
        try {
          // Refocus and re-place caret at the end
          el.focus();
          const len2 = el.value ? el.value.length : 0;
          el.setSelectionRange(len2, len2);
        } catch (_e) {}
      }, 0);
    };

    /**
     * Clear all inputs and focus the hidden input again.
     */
    const clearInputs = () => {
      setOtpRaw("");
      setInputDigits("");
      setTempValue("");
      // Don't focus the input on TV to prevent native keyboard from opening
      if (!isTvEnv()) {
        deferFocus(collectorRef.current);
      }

      if (onInputChange) {
        onInputChange();
      }
    };

    // Sync tempValue when otpRaw changes
    useEffect(() => {
      setTempValue(otpRaw);
    }, [otpRaw]);

    // Show keyboard when shouldShowKeyboard is true and input is focused
    useEffect(() => {
      if (shouldShowKeyboard && collectorRef.current) {
        const handleFocus = () => {
          setShouldShowOnScreenKeyboard(true);
          setTempValue(otpRaw);
        };

        const input = collectorRef.current;
        input.addEventListener("focus", handleFocus);
        return () => {
          input.removeEventListener("focus", handleFocus);
        };
      }
    }, [shouldShowKeyboard, otpRaw]);

    /**
     * Update OTP value and trigger callbacks
     * Memoized to avoid stale closure issues
     */
    const updateOTPValue = useCallback(
      (digitsOnly: string) => {
        setOtpRaw(digitsOnly);
        setInputDigits(digitsOnly);

        if (onInputChange) {
          onInputChange();
        }

        if (digitsOnly.length === 4) {
          // Pass the value directly to avoid race condition
          otpAutoSubmit(digitsOnly);
        }
      },
      [onInputChange],
    );

    const handleKeyboardDone = () => {
      const digitsOnly = tempValue.replace(/\D/g, "").slice(0, 4);
      updateOTPValue(digitsOnly);
    };

    /**
     * Handle TV keyboard input changes
     * Updates the temporary value, visible boxes, and hidden input in real-time
     * Auto-submits when 4 digits are entered
     */
    const handleTVKeyboardChange = useCallback(
      (value: string) => {
        const digitsOnly = value.replace(/\D/g, "").slice(0, 4);
        setTempValue(digitsOnly);
        // Update otpRaw immediately so the hidden input has the value
        setOtpRaw(digitsOnly);
        // Update visible boxes in real-time
        setInputDigits(digitsOnly);

        if (digitsOnly.length === 4) {
          // Ensure the hidden input has the value before submitting
          const input = collectorRef.current;
          if (input) {
            input.value = digitsOnly;
          }
          // Commit the value and trigger auto-submit
          updateOTPValue(digitsOnly);
        }
      },
      [updateOTPValue],
    );

    // Enable TV remote numeric keypad input for OTP
    useTvNumericInput(
      tempValue,
      handleTVKeyboardChange,
      4,
      !!shouldShowKeyboard,
    );

    useImperativeHandle(ref, () => ({
      clearInputs,
    }));

    useEffect(() => {
      (async () => {
        /**
         * If WebOTP is supported, listen for SMS OTP and fill inputs automatically.
         */
        if ("OTPCredential" in window) {
          const input = document.querySelector('input[id="otp-digit"]');
          if (!input) return;

          const ac = new AbortController();

          const form = input.closest("form");
          if (form) {
            form.addEventListener("submit", () => {
              ac.abort();
            });
          }

          navigator.credentials
            .get({
              otp: { transport: ["sms"] },
              signal: ac.signal,
            })
            .then((otp) => {
              const otpValue = (otp as any).code;

              setOtpRaw(otpValue);
              setInputDigits(otpValue);
              deferFocus(collectorRef.current);

              const formEl = input.closest("form");
              if (formEl) {
                formEl.requestSubmit();
              }
            })
            .catch((error) => {
              console.error("Error in navigator.credentials block", error);
            });
        }
      })();

      /**
       * Safari/iOS focus can be flaky, so try a few times to focus the collector input.
       * This helps with Safari/iOS focus issues
       * Skip auto-focus on TV as it's not needed
       */
      if (!isTvEnv()) {
        const focusFirstOtp = () => focusWithoutScroll(collectorRef.current);
        // Try immediately, then on next frame, then after a short delay
        focusFirstOtp();
        requestAnimationFrame(focusFirstOtp);
        // Adding a small delay to ensure focus is set correctly
        setTimeout(focusFirstOtp, 50);
      }
    }, []);

    /**
     * Handle typing in the hidden input: keep only digits, update visible boxes,
     * and auto-submit when 4 digits are entered.
     */
    const handleCollectorChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (shouldShowOnScreenKeyboard) {
        // If keyboard is open, don't handle direct input changes
        return;
      }
      const rawValue = "" + e.target.value;
      const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 4);
      updateOTPValue(digitsOnly);
    };

    const handleInputMouseDown = (e: MouseEvent<HTMLInputElement>) => {
      e.preventDefault();
      deferFocus(collectorRef.current);
    };

    return (
      <div className="relative">
        <div
          className="relative grid h-[4rem] auto-cols-fr grid-flow-col items-center justify-center gap-4 md:gap-3"
          onMouseDown={activateCollector}
          onClick={activateCollector}
          onTouchStart={activateCollector}
        >
          {/* Hidden input catches all typing and autofill for better UX */}
          <input
            ref={collectorRef}
            value={otpRaw}
            onChange={handleCollectorChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            maxLength={4}
            type="text"
            inputMode="numeric"
            autoFocus={!isTvEnv()}
            pattern="[0-9]*"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            id="otp-digit"
            name="otp-digit"
            autoComplete="one-time-code"
            aria-label="Enter 4-digit verification code"
            data-otp-value={otpRaw}
            className="absolute inset-0 z-10 h-full w-full appearance-none border-0 bg-transparent text-transparent caret-transparent outline-none"
          />

          {/*
        Read-only boxes show the OTP digits, mirroring the hidden input.
        No focus hopping or weird keyboard issues here.
      */}
          <input
            ref={input1Ref}
            value={input1}
            readOnly
            tabIndex={-1}
            onMouseDown={handleInputMouseDown}
            maxLength={1}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            id="otp-box-1"
            name="otp-box-1"
            className={`${baseInputClasses} "outline-white"`}
          />
          <input
            ref={input2Ref}
            value={input2}
            readOnly
            tabIndex={-1}
            onMouseDown={handleInputMouseDown}
            maxLength={1}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            id="otp-box-2"
            name="otp-box-2"
            className={`${baseInputClasses} ${input2 || input1 ? "outline-white" : "outline-stg-grey-3"}`}
          />
          <input
            ref={input3Ref}
            value={input3}
            readOnly
            tabIndex={-1}
            onMouseDown={handleInputMouseDown}
            maxLength={1}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            id="otp-box-3"
            name="otp-box-3"
            className={`${baseInputClasses} ${input3 || input2 ? "outline-white" : "outline-stg-grey-3"}`}
          />
          <input
            ref={input4Ref}
            value={input4}
            readOnly
            tabIndex={-1}
            onMouseDown={handleInputMouseDown}
            maxLength={1}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            id="otp-box-4"
            name="otp-box-4"
            className={`${baseInputClasses} ${input4 || input3 ? "outline-white" : "outline-stg-grey-3"}`}
          />
        </div>

        {/* Error message - Show below input boxes and above keyboard */}
        {errorMessage && (
          <div className="align-start  my-4 rounded  text-start text-sm text-stg-red-1">
            {errorMessage}
          </div>
        )}

        {/* TV Keyboard - Show when shouldShowKeyboard is true */}
        {shouldShowKeyboard && shouldShowOnScreenKeyboard && (
          <div className="mt-4">
            <TvVirtualKeyboard
              isFocusBoundary={true}
              value={tempValue}
              onChange={handleTVKeyboardChange}
              onDone={handleKeyboardDone}
              showActionButtons={false}
              numeric={true}
              onDownArrowFromLastRow={onDownArrowFromLastRow}
            />
          </div>
        )}
      </div>
    );
  },
);

OTPInput.displayName = "OTPInput";

/**
 * Submit the form automatically when 4 digits are entered.
 * @param otpValue - The OTP value to submit (optional, will read from DOM if not provided)
 *
 * To avoid race conditions, always pass the OTP value directly instead of reading from DOM.
 * This ensures the form submits with the correct value even if React hasn't updated the DOM yet.
 */
function otpAutoSubmit(otpValue?: string) {
  const input = document.querySelector(
    'input[id="otp-digit"]',
  ) as HTMLInputElement | null;
  if (!input) return;

  // If value is provided, use it directly (avoids race condition)
  // Otherwise, fallback to reading from displayed boxes
  let finalOtpValue = otpValue;

  if (!finalOtpValue) {
    // Fallback: Read values from the displayed input boxes
    const box1 = document.querySelector(
      'input[id="otp-box-1"]',
    ) as HTMLInputElement | null;
    const box2 = document.querySelector(
      'input[id="otp-box-2"]',
    ) as HTMLInputElement | null;
    const box3 = document.querySelector(
      'input[id="otp-box-3"]',
    ) as HTMLInputElement | null;
    const box4 = document.querySelector(
      'input[id="otp-box-4"]',
    ) as HTMLInputElement | null;

    // Construct the OTP value from the displayed boxes
    finalOtpValue = [
      box1?.value || "",
      box2?.value || "",
      box3?.value || "",
      box4?.value || "",
    ].join("");
  }

  // Set the hidden input value and submit
  if (finalOtpValue && finalOtpValue.length === 4) {
    input.value = finalOtpValue;
    const form = input.closest("form");
    if (form) {
      form.requestSubmit();
    }
  }
}
